import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SearchOpportunitiesCalendarParameters, SearchOpportunitiesCalendarResult } from '@api-proxies';
import { OpportunityService } from '@http/clients/opportunity.service';
import { AwaiterService } from '@services/awaiter.service';
import { FilterStateService } from '@services/filter-state.service';
import { FilterValueOpportunities } from '@shared/opportunities-filters/opportunity-filters.config';
import { OpportunityCardCompactComponent } from '@shared/opportunity-card/opportunity-card-compact/opportunity-card-compact.component';
import {
    DateRanges,
    DateTimeService,
    FullViewCalendarComponent,
    FullViewCalendarViewType,
    IFullViewCalendarEvent
} from 'ngx-lib';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-opportunities-calendar',
    templateUrl: './opportunities-calendar.component.html',
    styleUrls: ['./opportunities-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FullViewCalendarComponent, OpportunityCardCompactComponent]
})
export class OpportunitiesCalendarComponent implements OnInit, OnDestroy {
    filters?: FilterValueOpportunities;
    searchConfig: SearchOpportunitiesCalendarParameters;
    events?: IFullViewCalendarEvent<SearchOpportunitiesCalendarResult>[];
    currentRange?: DateRanges;
    currentView: FullViewCalendarViewType;

    private filterSubscription!: Subscription;

    constructor(
        private readonly opportunityService: OpportunityService,
        private readonly dateTimeService: DateTimeService,
        private readonly awaiter: AwaiterService,
        private readonly filterStateService: FilterStateService<FilterValueOpportunities>
    ) {
        this.searchConfig = {} as SearchOpportunitiesCalendarParameters;
        this.currentView = 'week';
    }

    async ngOnInit(): Promise<void> {
        this.filterSubscription = this.filterStateService.stateFilters$.subscribe((newFilters) => {
            if (!newFilters) return;
            this.filters = { ...newFilters };
            this.refreshCalendarView();
        });
    }

    ngOnDestroy(): void {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
    }

    async refreshCalendarView(): Promise<void> {
        this.buildSearchConfig();
        await this.awaiter.awaitAction('Getting Calendar Opportunities', async () => {
            const result = await this.opportunityService.searchCalendar({
                ...this.searchConfig,
                ...(this.getDateRanges() as unknown as Date)
            } as SearchOpportunitiesCalendarParameters);

            this.events = result.map((opportunity: SearchOpportunitiesCalendarResult) => ({
                title: opportunity.name,
                description: opportunity.id.toString(),
                data: opportunity,
                date: this.dateTimeService.getDateTimeFromString(opportunity.eventDate.toString()),
                startTime: this.dateTimeService.timeOnly(opportunity.eventDate)
            }));
        });
    }

    async onDateRangeSelected(range: DateRanges): Promise<void> {
        this.currentRange = range;
        this.filterStateService.updateMainFilters({ ...this.filters });
    }

    private getDateRanges(): { dueDateFrom: string | null; dueDateTo: string | null } {
        if (this.currentRange?.dateFrom || this.currentRange?.dateTo) {
            return {
                dueDateFrom: this.currentRange?.dateFrom,
                dueDateTo: this.currentRange?.dateTo
            };
        } else {
            return this.getDefaultDateRanges();
        }
    }

    private getDefaultDateRanges(): { dueDateFrom: string | null; dueDateTo: string | null } {
        if (this.currentView === 'day')
            return {
                dueDateFrom: this.dateTimeService.today().startOf('day').toISO(),
                dueDateTo: this.dateTimeService.today().endOf('day').toISO()
            };

        if (this.currentView === 'week')
            return {
                dueDateFrom: this.dateTimeService.getStartOfWeek(this.dateTimeService.now()).toISO(),
                dueDateTo: this.dateTimeService.getEndOfWeek(this.dateTimeService.now()).toISO()
            };

        return {
            dueDateFrom: this.dateTimeService.getStartOfMonth(this.dateTimeService.now(), true).toISO(),
            dueDateTo: this.dateTimeService.getEndOfMonth(this.dateTimeService.now(), true).toISO()
        };
    }

    private buildSearchConfig(): void {
        this.searchConfig = {
            filterText: this.filters?.searchText ?? undefined,
            opportunityStatusIds: this.filters?.statuses?.length
                ? this.filters?.statuses.map((s: any) => s.id)
                : undefined,
            divisionIds: this.filters?.departments?.length
                ? this.filters?.departments.map((d: any) => d.id)
                : undefined,
            probabilityValueFrom: this.filters?.probabilityFrom,
            probabilityValueTo: this.filters?.probabilityTo,
            onlyFavorites: this.filters?.onlyFavorites,
            includeArchived: this.filters?.includeArchived ?? undefined,
            companyIds: this.filters?.companies?.length ? this.filters?.companies.map((c: any) => c.id) : undefined,
            potentialValueFrom: this.filters?.potentialValueFrom,
            potentialValueTo: this.filters?.potentialValueTo,
            proposedGrossMarginFrom: this.filters?.proposedGrossMarginFrom,
            proposedGrossMarginTo: this.filters?.proposedGrossMarginTo,
            expectedResponseDateFrom: this.filters?.expectedResponseDateFrom,
            expectedResponseDateTo: this.filters?.expectedResponseDateTo,
            salespersonIds: this.filters?.salespersons?.length
                ? this.filters?.salespersons.map((s: any) => s.externalId)
                : undefined,
            accountIds: this.filters?.accounts?.length ? this.filters?.accounts.map((a: any) => a.id) : undefined,
            companyRoleIds: this.filters?.companyRoles?.length
                ? this.filters?.companyRoles?.map((c: any) => c.id)
                : undefined
        } as SearchOpportunitiesCalendarParameters;
    }
}
